import { NavigateFunction, useLocation } from 'react-router-dom';
import { grey } from '@mui/material/colors';


export enum DiagnosticSeverity {
    Info = 0,
    Warning = 1,
    Error = 2
}

export interface DiagnosticValidationFailure {
    propertyName: string;
    errorMessage: string;
    severity: DiagnosticSeverity;
    etlStep: number;
}

export interface DiagnosticValidationResult {
    isValid: boolean;
    failures: DiagnosticValidationFailure[];
    lastKnownChange?: Date;
}

export interface TupleCountCutterStates {
    item1: number;
    item2: CutterState[]
}

export interface CutterState {
    serialNumber: string;
    latestNewsDays: number;
    latestEventDays?: number;
    lisA_LastStatusReport: Date;
    lisA_LastRoutineReport: Date;
    lisA_EvManagerVersion: string;
    lisA_LastReport: Date;
    lisA_YearWeek: string;
    lisA_Contract: string;
    lisA_Country: string;
    lisA_LastReportInDays?: number;
    lisA_LastKnownChange: Date;
    cusT_User: string;
    cusT_Contact: string;
    hasContract?: number;
    cusT_Category: string;
    cusT_CompanyName: string;
    cusT_CompanyId: string;
    cusT_PlantName: string;
    cusT_LastKnownChange?: Date;
    cusT_CountryCode: string;
    cutR_ZipUploadDate: Date;
    cutR_ExtractDate: Date;
    cutR_ZipFileName: string;
    cutR_RoutineLast: Date;
    cutR_RoutineNext: Date;
    cutR_BackupLast: Date;
    cutR_BackupNext: Date;
    cutR_YearWeek: string;
    cutR_ZipCreationDate: Date;
    cutR_UploadDaysDelay?: number;
    cutR_UploadDaysLast?: number;
    cutR_ZipStoredOnDisk?: boolean;
    cutR_ZipComment?: string;
    cutR_ZipErrorCode?: string;
    cutR_ZipErrorDate?: Date;
    cutR_EffectiveCuttingTime?: number;
    cutR_VacuumTime?: number;
    cutR_LastKnownChange?: Date;
    intE_LastIssueDays?: number;
    intE_LastIssueZipReport: string;
    intE_LastIssueSourceLogFile: string;
    intE_ETL_STEP: number;
    intE_Log: string;
    intE_Attempts: number;
    intE_LastKnownChange?: Date;
    sofT_Group: string;
    sofT_Main_Feature_HostId: string;
    sofT_CNUM_Version: string;
    sofT_Pilot_Version: string;
    sofT_CMachine_Version: string;
    sofT_Maintenance_Version: string;
    sofT_Bibliobus_Version: string;
    sofT_Events_Manager_Version: string;
    sofT_Alerts_Manager_Version: string;
    sofT_Status_Report_Manager_Version: string;
    sofT_Commercial_Version: string;
    sofT_LastKnownChange?: Date;
    sofT_VERS_LSVVersion: string;
    sofT_VERS_LNestVersion: string;
    sofT_VERS_LScanVersion: string;
    sofT_VERS_LCMachineVersion: string;
    sofT_VERS_OffloadVersion: string;
    sofT_VERS_LDBSrvVersion: string;
    sofT_VERS_DataManagerVersion: string;
    sofT_VERS_ReportsManagerVersion: string;
    sofT_VERS_LastKnownChange: string;
    harD_CUT_Machine: string;
    harD_CUT_SoldDate?: string;
    harD_Machine_type: string;
    harD_Bibliobus_Machine: string;
    harD_Events_Manager_Machine: string;
    harD_Shared?: number;
    harD_LastKnownChange?: Date;
    harD_Model: string;
    harD_Market: string;
    harD_Product: string;
    harD_Size?: number;
    harD_Variant: string;
    harD_OriginalMachineType?: string;
    netW_LastModif: string;
    netW_SliceKiB: string;
    netW_NeedProxy?: number;
    netW_ProxyAuthentication?: number;
    netW_ProxyHost: string;
    netW_ProxyPort?: number;
    netW_StandardThroughput?: number;
    netW_SmtpServer: string;
    netW_SmtpPort?: number;
    netW_SmtpAuthentication?: number;
    netW_From: string;
    netW_SmtpLogin: string;
    netW_SmtpCryptography: string;
    netW_LastKnownChange: Date;
    dasH_LastEventDate: Date;
    dasH_LastEventYearWeek: string;
    dasH_LastKnownChange: Date;
    SalesForceCaseCount?: number;
    stats_SoftwareUpdateIndex?: number;
    stats_ConnectionRate?: number;
    diagnostics?: DiagnosticValidationResult;
}

export const getMarketAvatar = (cutter: CutterState): { label: string, color: string } => {
    switch (cutter.harD_Market) {
        case 'Auto':
            return { label: 'Au', color: '#be1622' }; /* DAM : #341622 */
        case 'Composite':
            return { label: 'Com', color: '' };
        case 'Denim':
            return { label: 'Den', color: '#6F8FAF' };
        case 'Fashion':
            return { label: 'Fa', color: '#460E0F' };
        case 'Footwear':
            return { label: 'Fw', color: '#8A7E72' };
        case 'Furniture':
            return { label: 'Fu', color: '#5C4D4E' }; /* DAM : #6C5861 */
        case 'Lingerie':
            return { label: 'Lin', color: '#873379' };
        case 'TechTex':
            return { label: 'Tex', color: '#0E776B' };
        default:
            return { label: '?', color: grey[100] };;
    };
}

export const getCountryAvatar = (cutter: CutterState): string => {
    let country: string = cutter.cusT_CountryCode?.toLowerCase() ?? 'un';
    switch (country) {
        case 'ch':
            country = 'cn';
            break;
        case 'vi': /* U.S. Virgin Islands */
            country = 'vn'; /* VietNam */
            break;
        case 'sw': /* Swaziland ? */
            country = 'se'; /* Sweden */
            break;
        case ' m': /* Wrong entry in LISA DB */
            country = 'mx'; /* Mexico */
            break;
        case ' r': /* Wrong entry in LISA DB */
            country = 'ru'; /* Mexico */
            break;
        default:
    }
    // if (country.startsWith(' ') || country.trim() === '') {
    /* FIX WRONG ALERTMANAGER ENTRY */
    switch (cutter.lisA_Country.trim()) {
        case 'Bangladesh':
            country = 'bd';
            break;
        case 'Bulgaria':
            country = 'bg';
            break;
        case 'Brazil':
            country = 'br';
            break;
        case 'Cambodia':
            country = 'kh';
            break;
        case 'China':
            country = 'cn';
            break;
        case 'El Salvador':
            country = 'sv';
            break;
        case 'France':
            country = 'fr';
            break;
        case 'India':
            country = 'in';
            break;
        case 'Indonesia':
            country = 'id';
            break;
        case 'Italy':
            country = 'it';
            break;
        case 'Lithuania':
            country = 'lt';
            break;
        case 'Mexico':
            country = 'mx';
            break;
        case 'Morocco':
            country = 'ma';
            break;
        case 'Pakistan':
            country = 'pk';
            break;
        case 'Poland':
            country = 'pl';
            break;
        case 'Portugal':
            country = 'pt';
            break;
        case 'Romania':
            country = 'ro';
            break;
        case 'Serbia':
            country = 'rs';
            break;
        case 'Slovakia':
            country = 'sk';
            break;
        case 'Slovenia':
            country = 'si';
            break;
        case 'South Africa':
            country = 'za';
            break;
        case 'Spain':
            country = 'es';
            break;
        case 'Sweden':
            country = 'se';
            break;
        case 'Switzerland':
            country = 'ch';
            break;
        case 'Thailand':
            country = 'th';
            break;
        case 'Turkey':
            country = 'tr';
            break;
        case 'Tunisia':
            country = 'tn';
            break;
        case 'United States':
            country = 'us';
            break;
        case 'United Kingdom':
            country = 'gb';
            break;
        case 'Uruguay':
            country = 'uy';
            break;
        case 'Viet Nam':
            country = 'vn';
            break;
        default:
    }
    // }
    return `url('${process.env.PUBLIC_URL}/flags/${country}.svg')`;
};

export const getModelPicture = (cutter: CutterState): string => {
    let model: string = cutter.harD_Model?.toLowerCase() ?? 'vector';
    // if (model.startsWith('virga')) { model = 'virga'; }
    switch (model) {
        case 'vector':
            model = 'Vector_iP9.png';
            break;
        case 'virga': /* U.S. Virgin Islands */
            model = 'Virga.jpg'; /* VietNam */
            break;
        case 'versalis': /* Swaziland ? */
            model = 'VersalisEVO.jpg'; /* Sweden */
            break;
        case 'focus': /* Wrong entry in LISA DB */
            model = 'Focus.jpg'; /* Mexico */
            break;
        default:
            model = 'Vector_iP9.png';
    }

    return `${process.env.PUBLIC_URL}/cuttermodels/${model}`;
};

export interface BookmarkNavigation {
    serialNumber: string;
    tab: string;
    subtab: string;
    rowId: number | null;
    rowSource: string;
}

// Hook to read bookmark deep-navigation params from URL query string
export const useBookmarkNavigation = (): BookmarkNavigation => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const rawRowId = queryParams.get('rowid');
    return {
        serialNumber: queryParams.get('serialnumber') ?? '',
        tab: queryParams.get('tab') ?? '',
        subtab: queryParams.get('subtab') ?? '',
        rowId: rawRowId !== null ? parseInt(rawRowId, 10) : null,
        rowSource: queryParams.get('rowsource') ?? '',
    };
};

// NavigateFunction must be passed as argument to avoid following error :
// Invalid hook call. Hooks can only be called inside of the body of a function component.
export const navigateTabsWithQueries = (navigate: NavigateFunction, action: string, argument: string, tab?: string, subtab?: string): void => {
    switch (action) {
        case 'search':
            navigate(`/search?searchstring=${encodeURIComponent(argument.trim())}`);
            break;
        case 'bookmarks': {
            let url = `/bookmarks?serialnumber=${encodeURIComponent(argument.trim())}`;
            if (tab) url += `&tab=${encodeURIComponent(tab)}`;
            if (subtab) url += `&subtab=${encodeURIComponent(subtab)}`;
            navigate(url);
            break;
        }
        case 'stats':
            navigate(`/stats?stats=${encodeURIComponent(argument.trim())}`);
            break;
        default:
    };
}

export const cutterStateColumns = [
    { key: 'serialNumber', header: 'SerialNumber' },
    { key: 'latestNewsDays', header: 'Last Report' },
    { key: 'latestEventDays', header: 'Last Event' },
    { key: 'lisA_LastStatusReport', header: 'LastStatusReport' },
    { key: 'lisA_LastRoutineReport', header: 'LastRoutineReport' },
    { key: 'lisA_LastReport', header: 'LastReport' },
    { key: 'lisA_YearWeek', header: 'YearWeek' },
    { key: 'lisA_Contract', header: 'Contract' },
    { key: 'lisA_Country', header: 'Country' },
    { key: 'lisA_LastReportInDays', header: 'LastReportInDays' },
    { key: 'lisA_LastKnownChange', header: 'LastKnownChange' },
    { key: 'cusT_User', header: 'User' },
    { key: 'cusT_Contact', header: 'Contact' },
    { key: 'hasContract', header: 'hasContract' },
    { key: 'cusT_Category', header: 'Category' },
    { key: 'cusT_CompanyName', header: 'CompanyName' },
    { key: 'cusT_CompanyId', header: 'CompanyId' },
    { key: 'cusT_PlantName', header: 'PlantName' },
    { key: 'cusT_LastKnownChange', header: 'LastKnownChange' },
    { key: 'cusT_CountryCode', header: 'CountryCode' },
    { key: 'cutR_ZipUploadDate', header: 'ZipUploadDate' },
    { key: 'cutR_ExtractDate', header: 'ExtractDate' },
    { key: 'cutR_ZipFileName', header: 'ZipFileName' },
    { key: 'cutR_RoutineLast', header: 'RoutineLast' },
    { key: 'cutR_RoutineNext', header: 'RoutineNext' },
    { key: 'cutR_BackupLast', header: 'BackupLast' },
    { key: 'cutR_BackupNext', header: 'BackupNext' },
    { key: 'cutR_YearWeek', header: 'YearWeek' },
    { key: 'cutR_ZipCreationDate', header: 'ZipCreationDate' },
    { key: 'cutR_UploadDaysDelay', header: 'UploadDaysDelay' },
    { key: 'cutR_UploadDaysLast', header: 'UploadDaysLast' },
    { key: 'cutR_ZipStoredOnDisk', header: 'ZipStoredOnDisk' },
    { key: 'cutR_ZipComment', header: 'ZipComment' },
    { key: 'cutR_ZipErrorCode', header: 'ZipErrorCode' },
    { key: 'cutR_ZipErrorDate', header: 'ZipErrorDate' },
    { key: 'cutR_EffectiveCuttingTime', header: 'EffectiveCuttingTime' },
    { key: 'cutR_VacuumTime', header: 'VacuumTime' },
    { key: 'cutR_LastKnownChange', header: 'LastKnownChange' },
    { key: 'intE_LastIssueDays', header: 'LastIssueDays' },
    { key: 'intE_LastIssueZipReport', header: 'LastIssueZipReport' },
    { key: 'intE_LastIssueSourceLogFile', header: 'LastIssueSourceLogFile' },
    { key: 'intE_ETL_STEP', header: 'ETL_STEP' },
    { key: 'intE_TalentStep', header: 'TalentStep' },
    { key: 'intE_Log', header: 'Log' },
    { key: 'intE_Attempts', header: 'Attempts' },
    { key: 'intE_LastKnownChange', header: 'LastKnownChange' },
    { key: 'sofT_Group', header: 'Group' },
    { key: 'sofT_Main_Feature_HostId', header: 'Main_Feature_HostId' },
    { key: 'sofT_CNUM_Version', header: 'CNUM_Version' },
    { key: 'sofT_Pilot_Version', header: 'Pilot_Version' },
    { key: 'sofT_CMachine_Version', header: 'CMachine_Version' },
    { key: 'sofT_Maintenance_Version', header: 'Maintenance_Version' },
    { key: 'sofT_Bibliobus_Version', header: 'Bibliobus_Version' },
    { key: 'sofT_Events_Manager_Version', header: 'Events_Manager_Version' },
    { key: 'sofT_Alerts_Manager_Version', header: 'Alerts_Manager_Version' },
    { key: 'sofT_Status_Report_Manager_Version', header: 'Status_Report_Manager_Version' },
    { key: 'sofT_LastKnownChange', header: 'LastKnownChange' },
    { key: 'sofT_VERS_LSVVersion', header: 'LSVVersion' },
    { key: 'sofT_VERS_LNestVersion', header: 'LNestVersion' },
    { key: 'sofT_VERS_LScanVersion', header: 'LScanVersion' },
    { key: 'sofT_VERS_LCMachineVersion', header: 'LCMachineVersion' },
    { key: 'sofT_VERS_OffloadVersion', header: 'OffloadVersion' },
    { key: 'sofT_VERS_LDBSrvVersion', header: 'LDBSrvVersion' },
    { key: 'sofT_VERS_DataManagerVersion', header: 'DataManagerVersion' },
    { key: 'sofT_VERS_ReportsManagerVersion', header: 'ReportsManagerVersion' },
    { key: 'sofT_VERS_LastKnownChange', header: 'LastKnownChange' },
    { key: 'harD_CUT_Machine', header: 'CUT_Machine' },
    { key: 'harD_CUT_SoldDate', header: 'CUT_SoldDate' },
    { key: 'harD_Machine_type', header: 'Machine_type' },
    { key: 'harD_Bibliobus_Machine', header: 'Bibliobus_Machine' },
    { key: 'harD_Events_Manager_Machine', header: 'Events_Manager_Machine' },
    { key: 'harD_Shared', header: 'Shared' },
    { key: 'harD_LastKnownChange', header: 'LastKnownChange' },
    { key: 'harD_Model', header: 'Model' },
    { key: 'harD_Market', header: 'Market' },
    { key: 'harD_Product', header: 'Product' },
    { key: 'harD_Size', header: 'Size' },
    { key: 'harD_Variant', header: 'Variant' },
    { key: 'netW_LastModif', header: 'LastModif' },
    { key: 'netW_SliceKiB', header: 'SliceKiB' },
    { key: 'netW_NeedProxy', header: 'NeedProxy' },
    { key: 'netW_ProxyAuthentication', header: 'ProxyAuthentication' },
    { key: 'netW_ProxyHost', header: 'ProxyHost' },
    { key: 'netW_ProxyPort', header: 'ProxyPort' },
    { key: 'netW_StandardThroughput', header: 'StandardThroughput' },
    { key: 'netW_SmtpServer', header: 'SmtpServer' },
    { key: 'netW_SmtpPort', header: 'SmtpPort' },
    { key: 'netW_SmtpAuthentication', header: 'SmtpAuthentication' },
    { key: 'netW_From', header: 'From' },
    { key: 'netW_SmtpLogin', header: 'SmtpLogin' },
    { key: 'netW_SmtpCryptography', header: 'SmtpCryptography' },
    { key: 'netW_LastKnownChange', header: 'LastKnownChange' },
    { key: 'dasH_LastEventDate', header: 'LastEventDate' },
    { key: 'dasH_LastEventYearWeek', header: 'LastEventYearWeek' },
    { key: 'dasH_LastKnownChange', header: 'LastKnownChange' }
];
